import { AppHeader } from '../components/layout/AppHeader';
import { PageShell } from '../components/layout/PageShell';
import { AnalysisNarrative } from '../components/market/AnalysisNarrative';
import { BreakthroughPanel } from '../components/market/BreakthroughPanel';
import { CandleTimePanel } from '../components/market/CandleTimePanel';
import { CandidateHistoryPanel } from '../components/market/CandidateHistoryPanel';
import { CandleStudyPanel } from '../components/market/CandleStudyPanel';
import { CandlestickChart } from '../components/market/CandlestickChart';
import { DirectionBadge, FamilyBadge } from '../components/market/CandidateBadges';
import { FundamentalsPanel } from '../components/market/FundamentalsPanel';
import { MarketDriverPanel } from '../components/market/MarketDriverPanel';
import { MetricCard } from '../components/market/MetricCard';
import { SupportResistancePanel } from '../components/market/SupportResistancePanel';
import { SupportingMethodsPanel } from '../components/market/SupportingMethodsPanel';
import { TimeframeMatrix } from '../components/market/TimeframeMatrix';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Panel } from '../components/ui/Panel';
import { CHART_INTERVALS } from '../config/timeframes';
import { useCoinAnalysis } from '../hooks/useCoinAnalysis';
import { navigate } from '../hooks/useHashRoute';
import { formatCompact, formatCurrency, formatPercent, formatPrice } from '../lib/format';
import { getSnapshot } from '../services/radarStore';

function fundingLabel(rate?: number): string {
  if (rate === undefined) return 'Unavailable';
  return `${formatPercent(rate * 100, 4)} per funding period`;
}

export function CoinPage({ symbol }: { symbol: string }): React.JSX.Element {
  const analysis = useCoinAnalysis(symbol);
  const { detail } = analysis;
  const hasResolvedPrice = detail.price > 0;
  const changeClass = detail.change24h > 0 ? 'metric-positive' : detail.change24h < 0 ? 'metric-negative' : '';
  const sourceTone = analysis.sourceMode === 'live' ? 'positive' : analysis.sourceMode === 'mixed' ? 'info' : 'warning';
  const historyLabel = analysis.sourceMode === 'live'
    ? 'verified histories'
    : analysis.sourceMode === 'mixed'
      ? 'partial histories'
      : 'history unavailable';
  const verifiedDexIdentity = detail.dex.identityConfidence === 'verified-contract';
  const predictor = getSnapshot().predictor.find((c) => c.symbol === detail.symbol);
  const streamTone = analysis.streamStatus === 'live'
    ? 'positive'
    : analysis.streamStatus === 'reconnecting' || analysis.streamStatus === 'connecting'
      ? 'info'
      : analysis.streamStatus === 'unavailable'
        ? 'negative'
        : 'warning';

  return (
    <div className="app-shell">
      <AppHeader connectionLabel={`${historyLabel} · ${analysis.streamStatus} stream`} currentSymbol={detail.baseAsset} />
      <PageShell>
        <div className="page-stack">
          <Button variant="ghost" onClick={() => navigate('/')} style={{ width: 'max-content' }}>← Back to opportunity tables</Button>

          <section className="detail-header">
            <div className="detail-identity">
              <span className="coin-avatar">{detail.baseAsset.slice(0, 2)}</span>
              <div>
                <h1 className="detail-title">{detail.fundamentals.name ?? detail.baseAsset}</h1>
                <p className="detail-subtitle">{detail.symbol} · live chart study and observed market participation</p>
              </div>
            </div>
            {predictor && (
              <div className="detail-direction-block">
                <DirectionBadge direction={predictor.direction} />
                <FamilyBadge family={predictor.family} />
              </div>
            )}
            <div className="price-block">
              <strong>{hasResolvedPrice ? `$${formatPrice(detail.price)}` : 'Unavailable'}</strong>
              <span className={changeClass}>{hasResolvedPrice ? `${formatPercent(detail.change24h)} over 24h` : 'No resolved live market price'}</span>
            </div>
          </section>

          {analysis.error && <div className="notice">{analysis.error}</div>}

          <section className="metric-grid" aria-label="Instant coin information">
            <MetricCard label="Current price" value={hasResolvedPrice ? `$${formatPrice(detail.price)}` : 'Unavailable'} note="Primary resolved public market" />
            <MetricCard label="24h change" value={<span className={changeClass}>{formatPercent(detail.change24h)}</span>} note="Context only, not candidate direction" />
            <MetricCard label="Observed 24h volume" value={formatCurrency(detail.totalObservedVolume24h)} note="Spot + futures + contract-verified DEX pools" />
            <MetricCard label="CEX spot volume" value={formatCurrency(detail.spotVolume24h)} note="Public spot turnover" />
            <MetricCard label="CEX futures volume" value={formatCurrency(detail.futuresVolume24h)} note="Public perpetual turnover" />
            <MetricCard label="DEX volume" value={verifiedDexIdentity ? formatCurrency(detail.dex.volume24h) : detail.dex.pairCount > 0 ? 'Unverified identity' : 'Unavailable'} note={`${detail.dex.pairCount} pool${detail.dex.pairCount === 1 ? '' : 's'} · ${detail.dex.identityConfidence.replace('-', ' ')}`} />
            <MetricCard label="Current funding" value={fundingLabel(detail.derivatives.fundingRate)} note={detail.derivatives.fundingBehavior.replace('-', ' ')} />
            <MetricCard label="Open interest" value={formatCurrency(detail.derivatives.openInterest)} note="Current observed futures positioning" />
            <MetricCard label="Market cap" value={formatCurrency(detail.fundamentals.marketCap)} note={detail.fundamentals.source} />
            <MetricCard label="Circulating supply" value={formatCompact(detail.fundamentals.circulatingSupply)} note="Source confidence shown below" />
            <MetricCard label="Maximum supply" value={formatCompact(detail.fundamentals.maxSupply)} note="Unavailable when not reliably published" />
            <MetricCard label="DEX liquidity" value={formatCurrency(detail.dex.liquidityUsd)} note={detail.dex.topPair ?? detail.dex.source} />
            <MetricCard label={`${analysis.selectedInterval.toUpperCase()} RSI`} value={Number.isFinite(analysis.currentRsi) ? analysis.currentRsi.toFixed(1) : 'Unavailable'} note="Context helper only; never a standalone signal" />
          </section>

          <div className="analysis-grid">
            <div className="page-stack">
              <Panel
                title={`${detail.symbol} fluid chart`}
                description="Timeframes include 1D and 1W as wider pressure reflectors; short horizons remain independently studied."
                className="chart-shell"
                action={
                  <div className="panel-badge-row">
                    <Badge tone={sourceTone}>{historyLabel}</Badge>
                    <Badge tone={streamTone}>{analysis.streamStatus}</Badge>
                  </div>
                }
              >
                <div className="chart-toolbar">
                  <div className="segmented-control" aria-label="Chart interval">
                    {CHART_INTERVALS.map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        data-active={analysis.selectedInterval === item.value}
                        onClick={() => analysis.setSelectedInterval(item.value)}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                  <span className="status-pill" title={analysis.streamMessage}>
                    <span className="status-dot" />
                    {analysis.selectedInterval.toUpperCase()} candle · {analysis.candleTiming.maturity.toFixed(0)}% mature · {analysis.streamStatus}
                  </span>
                </div>
                {analysis.loading ? (
                  <div className="loading-block" />
                ) : analysis.candles.length > 0 ? (
                  <CandlestickChart candles={analysis.candles} zones={analysis.zones} />
                ) : (
                  <div className="table-empty chart-empty">
                    <strong>Chart unavailable</strong>
                    <span>No verified candle history was returned for this market and timeframe.</span>
                  </div>
                )}
              </Panel>

              <Panel title="Timeframe direction and pressure" description="Candle color, closed-candle structure, maturity, time-normalized volume, and parent pressure are kept separate. 1D and 1W remain wider reflectors.">
                <TimeframeMatrix assessments={analysis.assessments} />
              </Panel>

              <AnalysisNarrative items={analysis.narrative} />
              <SupportResistancePanel zones={analysis.zones} />
              <SupportingMethodsPanel candidate={predictor} />
              <CandidateHistoryPanel symbol={detail.symbol} />
            </div>

            <aside className="sidebar-stack">
              <CandleTimePanel interval={analysis.selectedInterval} timing={analysis.candleTiming} />
              <CandleStudyPanel study={analysis.candleStudy} />
              <BreakthroughPanel breakthrough={analysis.breakthrough} />
              <MarketDriverPanel detail={detail} />
              <FundamentalsPanel detail={detail} />
              <Panel title="Research boundary" description="Pilum filters and monitors possible opportunities; it does not know the future or issue an entry command.">
                <div className="attribution-list">
                  <div className="notice">
                    Crypto direction can change suddenly because of leverage, liquidity, manipulation, news, and wider market pressure. Review the evidence, support and resistance scenarios, and perform your own research.
                  </div>
                </div>
              </Panel>
            </aside>
          </div>
        </div>
      </PageShell>
    </div>
  );
}
